/**
 * Pure decision logic for whether an auth-flow failure (a failed silent token
 * renewal, or an access token that expired without renewal) is worth recording
 * at all — extracted so the contract is unit-testable without the OIDC event
 * machinery. Mirrors `resolveAuthAction.ts`.
 *
 * Why this exists: `handleSilentRenewError` / `handleAccessTokenExpired` in
 * AuthProvider reported EVERY foreground token-expiry as a warning, regardless
 * of route. But an unauthenticated user sitting on a PUBLIC page (`/register`,
 * `/logged-out`) has no live session to renew — an expiring/expired stored
 * token there is the expected state, not a failure. That made
 * `[Auth] Access token expired without successful silent renewal`
 * (CUSTOMER-UI-57) the highest-volume issue at 59 events / 33 users, mostly on
 * `/register`. This gate suppresses those.
 *
 * As of 2026-08 neither handler raises a Sentry issue at all — both emit an
 * observable-only `noteSilentSignal` (see AuthProvider). This gate still
 * matters for two reasons: it keeps the unactionable classes out of the log
 * entirely, and a `"report"` decision is what ARMS the bounded mid-window
 * renewal re-attempt, which must never run for a session that is definitively
 * over.
 */
export type AuthFailureReportDecision =
  // Record the warning-level signal (and arm the mid-window re-attempt): a
  // genuine foreground renewal failure for a user who should have a live
  // session.
  | "report"
  // Do not alert: the user is on a public/unauthenticated route, so a dead
  // token is expected. Breadcrumb only.
  | "skip-public"
  // Do not alert: the renewal failed because the SSO session genuinely ended
  // (the refresh token was rejected — invalid_grant / "Token is not active").
  // The user simply re-authenticates; not an actionable failure. Breadcrumb
  // only. (CUSTOMER-UI-56 — replay 487874ce.)
  | "skip-session-ended"
  // Do not alert: background renewal was deliberately skipped while the tab was
  // hidden, so the expiry is the accepted consequence. Breadcrumb only.
  | "skip-background";

/**
 * Definitive "the SSO session is gone, re-authenticate" signals from
 * Keycloak/oidc-client-ts. These are the expected end of a long session, not a
 * transient/network failure — so they should not raise a Sentry warning.
 *
 * NOTE: a large share of these is expected churn by deliberate design — the
 * UserManager does NOT request `offline_access` (our Keycloak customer clients
 * can't grant it; requesting it loops sign-in — see
 * `oidc-no-offline-access-scope.md`), so refresh tokens die at the Keycloak SSO
 * Session Idle (~15min). The trend stays observable via the skip-path
 * `console.warn` (Kibana WARN + Mixpanel replay). This is the accepted cost of
 * the scope being ungrantable, mitigated by the other renewal-recovery layers
 * — it will not "drop on its own" unless Keycloak is ever reconfigured and the
 * opt-in enabled. That un-fixable-from-here quality is also why the surviving
 * `"report"` cases stopped raising Sentry issues in 2026-08.
 *
 * Matching is substring-on-message because the handler only receives
 * `error.message`; `invalid_grant`/`login_required`/`interaction_required` are
 * stable OIDC codes, while `token is not active`/`session not active` are
 * Keycloak copy that could shift across realm/version upgrades.
 */
const SESSION_ENDED_MARKERS = [
  "invalid_grant",
  "token is not active",
  "session not active",
  "login_required",
  "interaction_required",
];

export function isExpectedSessionEndError(message: string): boolean {
  const lower = message.toLowerCase();
  return SESSION_ENDED_MARKERS.some((marker) => lower.includes(marker));
}

export interface AuthFailureReportSignals {
  /**
   * Is the user on a public/unauthenticated route (`/register`, `/logged-out`)?
   * Token expiry there is expected and must not alert.
   */
  isPublicRoute: boolean;
  /**
   * Was a silent renewal deliberately skipped because the tab was backgrounded?
   * (Set by the `signinSilent` wrapper in AuthProvider.)
   */
  renewalSkippedWhileHidden: boolean;
  /** Is the tab currently hidden (`document.visibilityState === "hidden"`)? */
  docHidden: boolean;
  /**
   * The renewal error message, when available (silent-renew failures carry one;
   * a bare access-token-expired event does not). Used to recognise a genuine
   * session-end vs. a transient/network failure.
   */
  errorMessage?: string;
}

export function resolveAuthFailureReport({
  isPublicRoute,
  renewalSkippedWhileHidden,
  docHidden,
  errorMessage,
}: AuthFailureReportSignals): AuthFailureReportDecision {
  // Public route wins over everything: no session intent, no alert.
  if (isPublicRoute) return "skip-public";
  // The SSO session genuinely ended — an expected re-auth, not a failure.
  if (errorMessage && isExpectedSessionEndError(errorMessage)) {
    return "skip-session-ended";
  }
  // The accepted consequence of the background-renewal policy.
  if (renewalSkippedWhileHidden || docHidden) return "skip-background";
  return "report";
}
